// Storage Utilities - ES Module Version
import storageManager from './storageManager.js';

// Tracks the most recent in-flight trip-data write to IndexedDB.
// Async reads await this so they never read stale data while a fire-and-forget
// save is still pending (which would clobber the fresh in-memory cache).
let _pendingSave = null;

// After a direct write to storageManager (this layer bypasses DataManager), keep
// the backup layers — .trip file auto-save, shadow backup, OPFS — in sync. Most
// of the app saves through here, so without this the .trip file would freeze with
// the trip's initial content. Fire-and-forget; never blocks the save path.
function _triggerBackups() {
    import('./DataManager.js')
        .then(({ default: dm }) => dm.notifyActiveTripSaved())
        .catch(() => { /* backups are best-effort */ });
    // Signal a trip write so the Drive auto-backup can schedule a light text save.
    try { window.dispatchEvent(new CustomEvent('wayfare-trip-saved')); } catch { /* no-DOM */ }
}

const Storage = {
    // Check if localStorage is available
    isAvailable: function() {
        try {
            const test = '__localStorage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch {
            return false;
        }
    },

    // Generic get/set methods
    get: function(key, defaultValue = null) {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch {
            return defaultValue;
        }
    },

    set: function(key, value) {
        try {
            localStorage.setItem(key, JSON.stringify(value));
            return true;
        } catch {
            return false;
        }
    },

    remove: function(key) {
        try {
            localStorage.removeItem(key);
            return true;
        } catch {
            return false;
        }
    },

    // Get user settings
    getSettings: function() {
        return this.get('userSettings', {
            userName: 'TRAVELER',
            location: 'CITY',
            country: 'COUNTRY',
            theme: 'auto'
        });
    },

    // Save user settings
    saveSettings: function(settings) {
        return this.set('userSettings', settings);
    },

    // Get trip data (now uses IndexedDB via storageManager)
    getTripData: function() {
        // This is now synchronous fallback - will be replaced with async version
        // Return cached data or empty structure
        const cached = window.__cachedTripData;
        if (cached) {
            return cached;
        }

        // Return default structure
        return {
            photos: [],
            notes: [],
            challenges: [],
            reflections: [],
            timeline: {},
            luggage: [],
            groceries: [],
            gifts: [],
            lodgings: [],
            privateDocuments: [],
            revealedPois: [],
            surfacedPois: [],
            dayClosures: {},
            sealedAt: null,
            openThreads: [],
            travelInfo: {
                flights: [],
                cars: [],
                trains: [],
                buses: []
            }
        };
    },

    // Get trip data (async version - USE THIS)
    getTripDataAsync: async function() {
        try {
            // Wait for any in-flight save to commit so we never read stale data
            // and clobber a freshly-written cache (the save/re-read race).
            if (_pendingSave) {
                try { await _pendingSave; } catch { /* save's own fallback handles errors */ }
            }

            console.log('[Storage.getTripDataAsync] Initializing storageManager...');
            await storageManager.initialize();

            console.log('[Storage.getTripDataAsync] Getting active trip data...');
            const tripData = await storageManager.getActiveTripData();
            console.log('[Storage.getTripDataAsync] Active trip data:', tripData ? 'found' : 'null');

            if (tripData) {
                // Cache for synchronous access
                window.__cachedTripData = tripData;
                console.log('[Storage.getTripDataAsync] ✅ Cached trip data:', {
                    notes: tripData.notes?.length,
                    timeline: Object.keys(tripData.timeline || {})
                });
                return tripData;
            }

            console.log('[Storage.getTripDataAsync] No active trip data, returning defaults');
            // Return default structure
            const defaultData = {
                photos: [],
                notes: [],
                challenges: [],
                reflections: [],
                timeline: {},
                luggage: [],
                groceries: [],
                gifts: [],
                lodgings: [],
                privateDocuments: [],
                travelInfo: {
                    flights: [],
                    cars: [],
                    trains: [],
                    buses: []
                }
            };
            window.__cachedTripData = defaultData;
            return defaultData;
        } catch (error) {
            console.error('[Storage] ❌ Error getting trip data:', error);
            return this.getTripData(); // Fallback to synchronous
        }
    },

    // Save photo
    savePhoto: function(photoData) {
        const tripData = this.getTripData();
        tripData.photos.push({
            id: Date.now(),
            data: photoData,
            timestamp: new Date().toISOString()
        });
        return this.set('tripData', tripData);
    },

    // Save note
    saveNote: function(noteData) {
        const tripData = this.getTripData();
        tripData.notes.push({
            id: Date.now(),
            ...noteData,
            timestamp: new Date().toISOString()
        });
        return this.set('tripData', tripData);
    },

    // Save trip data (now uses IndexedDB via storageManager with retry)
    saveTripData: function(tripData) {
        // Cache for synchronous access
        window.__cachedTripData = tripData;

        const self = this;
        const doSave = async (retries) => {
            try {
                await storageManager.initialize();
                await storageManager.saveActiveTripData(tripData);
                console.log('[Storage] ✅ Trip data saved to IndexedDB');
                _triggerBackups();
            } catch (error) {
                console.error('[Storage] ❌ Error saving trip data:', error);
                if (retries > 0) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    return doSave(retries - 1);
                }
                self.set('tripData', tripData); // localStorage fallback
            }
        };

        // Track the in-flight write (including retries) so async reads can await it.
        const p = doSave(2);
        _pendingSave = p;
        p.finally(() => { if (_pendingSave === p) _pendingSave = null; });

        return true;
    },

    // Save trip data (async version - USE THIS)
    saveTripDataAsync: async function(tripData) {
        // Cache for synchronous access
        window.__cachedTripData = tripData;

        // Track the in-flight write so concurrent async reads wait for it.
        const p = (async () => {
            await storageManager.initialize();
            await storageManager.saveActiveTripData(tripData);
        })();
        _pendingSave = p;

        try {
            await p;
            console.log('[Storage] ✅ Trip data saved to IndexedDB');
            _triggerBackups();
            return true;
        } catch (error) {
            console.error('[Storage] ❌ Error saving trip data:', error);
            // Fallback: save to old localStorage key as backup
            return this.set('tripData', tripData);
        } finally {
            if (_pendingSave === p) _pendingSave = null;
        }
    },

    // Save photos to IndexedDB (NEW - for memory photos)
    savePhotosToIndexedDB: async function(photos, memoryTitle, memoryId) {
        try {
            console.log('📸 [Storage] Saving photos to IndexedDB:', photos.length);
            await storageManager.initialize();

            const activeId = storageManager.getActiveTripId();
            if (!activeId) {
                console.error('📸 [Storage] No active trip ID found');
                return false;
            }

            console.log('📸 [Storage] Active trip ID:', activeId);

            // Save each photo individually to the photos object store
            for (const photo of photos) {
                const photoEntry = {
                    id: photo.id,
                    tripId: activeId,
                    data: photo.data,
                    timestamp: photo.timestamp,
                    memoryTitle: memoryTitle,
                    memoryId: memoryId
                };
                console.log('📸 [Storage] Saving photo:', { id: photoEntry.id, hasData: !!photoEntry.data, timestamp: photoEntry.timestamp });
                await storageManager.savePhoto(photoEntry);
            }

            console.log('📸 [Storage] ✅ All photos saved to IndexedDB');
            return true;
        } catch (error) {
            console.error('📸 [Storage] ❌ Error saving photos:', error);
            return false;
        }
    },

    // Load a single photo by its primary key (for lazy display in notes)
    getPhotoById: async function(photoId) {
        try {
            await storageManager.initialize();
            return await storageManager.getPhoto(photoId);
        } catch { return null; }
    },

    // Load the most recent N photos for the home carousel
    getRecentPhotos: async function(limit = 20) {
        try {
            await storageManager.initialize();
            const tripId = storageManager.getActiveTripId();
            if (!tripId) return [];
            return await storageManager.getRecentPhotosByTrip(tripId, limit);
        } catch { return []; }
    },

    // Mark challenge as complete
    markChallengeComplete: function(challengeId) {
        const tripData = this.getTripData();
        if (!tripData.challenges.includes(challengeId)) {
            tripData.challenges.push(challengeId);
            return this.set('tripData', tripData);
        }
        return true;
    }
};

// Make available globally for backward compatibility
window.Storage = Storage;

export default Storage;